import { useAppTranslation } from '@hooks/useAppTranslation';
import { useEffect, useState } from 'react';
import { getForm, getForms, GetFormsPayloadType } from '@api';
import { getStateKeyByValue } from '@utils';
import { FormState, getWirkUrl } from '@definitions';
import openLink from '@utils/openLink';
import { useParams } from 'react-router';
import { FormType } from '@pages/types';
import { css } from '@emotion/react';
import { Button, Modal, Space, Text, colors } from 'common-components';

import FiltersBarForm from '../FiltersBarForm';
import CardFormSelected from '../CardFormSelected';

import CardForm from './CardForm';

import { formApiMapper } from '@/mappers';
import { store } from '@/store';

type FormSelectorPropsType = {
  idFormSelectedInit?: string
  onFormSelect: (form: FormType) => void
  useDisclosure: { isOpen: boolean; open: () => void; close: () => void }
};

const FormSelector = ({
  idFormSelectedInit,
  onFormSelect,
  useDisclosure,
}: FormSelectorPropsType) => {
  const accessToken = store.use.me().accessToken;
  const paramsUrl = useParams();
  const { translate } = useAppTranslation();

  const [idFormSelected, setIdFormSelected] = useState<string>(idFormSelectedInit);
  const [formSelected, setFormSelected] = useState<FormType>();
  const [forms, setForms] = useState<FormType[]>([]);

  const [params, setParams] = useState<GetFormsPayloadType>({
    model: { state: [getStateKeyByValue(FormState, 'published')] },
  });

  useEffect(() => {
    if (idFormSelected) {
      getForm(idFormSelected).then((formApi) => {
        const result = formApiMapper(formApi);
        setFormSelected(result);
        onFormSelect(result);
      });
    }
  }, [idFormSelected]);

  useEffect(() => {
    getForms(params).then((formsApi) => {
      const result = formsApi?.map(formApiMapper);
      setForms(result);
    });
  }, [JSON.stringify(params)]);

  const onParamsChange = (newParams: GetFormsPayloadType) => {
    const merge = { ...params, ...newParams };
    setParams({
      ...merge,
      model: {
        ...merge.model,
      },
    });
  };

  const onFormPreview = (idForm: string) =>
    openLink(
      `${getWirkUrl('Form')}/form/${idForm}/preview?token=${accessToken}&tenant=${
        paramsUrl.tenant
      }`,
    );

  return (
    <>
      <Space style={{ width: '100%' }} size={8} orientation={'vertical'}>
        {!!formSelected?.template?.template && (
          <CardFormSelected templateFrom={formSelected.template.template} />
        )}
        <Button onClick={useDisclosure.open} size="sm">
          {translate('form.chooseFormButton')}
        </Button>
      </Space>
      <Modal
        width={918}
        open={useDisclosure.isOpen}
        onOk={useDisclosure.close}
        onCancel={useDisclosure.close}
        title={
          <Text color={colors.gray900} size="lg" style={{ fontWeight: 500 }}>
            {translate('form.formListTitle')}
          </Text>
        }
        footer={null}
        styles={{ container: { padding: 20, borderRadius: 12 }, header: { marginBottom: 16 } }}
      >
        <FiltersBarForm initValues={params} onFiltersChange={onParamsChange} />
        <div
          css={css`
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 12px 20px;
            margin-top: 16px;
          `}
        >
          {forms.map((form, i) => (
            <CardForm
              key={`${i}-${form.idForm}`}
              form={form}
              actions={[
                { icon: 'Eye', onClick: () => onFormPreview(form.idForm) },
                { icon: 'ArrowCircleBrokenRight', onClick: () => setIdFormSelected(form.idForm) },
              ]}
            />
          ))}
        </div>
      </Modal>
    </>
  );
};

export default FormSelector;
